import { useState } from 'react';
import {
	ResourceType,
	getResourceTypePermissions,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { Table } from './components/Table';
import { ProgrammeSettingsView } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MipManage.types';
import {
	useTranslationMipCreate,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	useProgrammeUsers,
	useDeleteProgrammeUser,
	useInviteProgrammeUser,
} from '@dealstack/frontend/app/pages/Project/MIP/hooks';
import { InviteTeamMemberDrawer } from '@dealstack/frontend/shared/components/InviteTeamMemberDrawer/InviteTeamMemberDrawer';
import { useParams } from 'react-router-dom';
import { StyledSettingsContainer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/styles.ts';

interface Props {
	onViewChange: (view: ProgrammeSettingsView, userId: string) => void;
}

export const ProgrammeUsers = ({ onViewChange }: Props) => {
	const tMipManage = useTranslationMipManage();
	const tMipCreate = useTranslationMipCreate();
	const { mipId } = useParams();

	const [isTeamMemberDrawerOpen, setIsTeamMemberDrawerOpen] = useState(false);

	const {
		data: programmeUsers,
		isLoading: isLoadingProgrammeUsers,
		refetch: refetchProgrammeUsers,
	} = useProgrammeUsers({
		programmeId: mipId!,
	});

	const programmePermissions = getResourceTypePermissions(ResourceType.PROGRAMME);
	const permissions = programmePermissions.map((permission) => ({
		label: tMipCreate(`invite_team_member.relation.${permission}`),
		value: permission,
	}));

	const { mutate: inviteProgrammeUser, isPending: isInvitationLoading } =
		useInviteProgrammeUser();
	const { mutate: deleteUser, isPending: isDeletingUser } = useDeleteProgrammeUser();

	const handleResendInvitation = (
		firstName: string,
		lastName: string,
		email: string,
		isContactPerson: boolean
	) => {
		inviteProgrammeUser(
			{
				firstName,
				lastName,
				email,
				programmeId: mipId!,
				isContactPerson,
			},
			{
				onSuccess: () => {
					showToastV2({
						message: tMipManage('root.programme_members.toast.invitation_resent'),
						state: 'success',
					});
				},
				onError: () => {
					showToastV2({
						message: tMipManage('root.programme_members.toast.invitation_failed'),
						state: 'error',
					});
				},
			}
		);
	};

	const handleRemoveUser = (userId: string) => {
		deleteUser(
			{
				userId,
				programmeId: mipId!,
			},
			{
				onSuccess: async () => {
					showToastV2({
						message: tMipManage('root.programme_members.toast.remove_success'),
						state: 'success',
					});
					await refetchProgrammeUsers();
				},
				onError: () => {
					showToastV2({
						message: tMipManage('root.programme_members.toast.remove_failed'),
						state: 'error',
					});
				},
			}
		);
	};

	const handleEditUser = (userId: string) => {
		onViewChange(ProgrammeSettingsView.User, userId);
	};

	const handleAddUserClick = () => {
		setIsTeamMemberDrawerOpen(true);
	};

	const handleProgrammeInvite = ({ firstName, lastName, email, role }, { onSuccess }) => {
		inviteProgrammeUser(
			{
				programmeId: mipId!,
				firstName,
				lastName,
				email,
				permissionRoles: [role],
				isContactPerson: false,
			},
			{
				onSuccess: async () => {
					await refetchProgrammeUsers();
					onSuccess?.();
				},
			}
		);
	};
	const isLoading = isLoadingProgrammeUsers || isInvitationLoading || isDeletingUser;

	return (
		<StyledSettingsContainer>
			<Table
				users={programmeUsers}
				isLoading={isLoading}
				onEditClick={handleEditUser}
				onResendInvitationClick={handleResendInvitation}
				onRemoveUserClick={handleRemoveUser}
				onAddUserClick={handleAddUserClick}
				onViewChange={onViewChange}
			/>
			<InviteTeamMemberDrawer
				isOpen={isTeamMemberDrawerOpen}
				setIsOpen={setIsTeamMemberDrawerOpen}
				isLoading={isLoading}
				permissions={permissions}
				onInvite={handleProgrammeInvite}
				description={tMipManage('invite_member_drawer.description')}
				roleLabel={tMipManage('invite_member_drawer.form.role_field.label')}
			/>
		</StyledSettingsContainer>
	);
};
